// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import LoadingSpinner from './components/LoadingSpinner';

// Import pages
import Dashboard from './components/Dashboard';
import FunMoneyPage from './pages/FunMoneyPage';
import SpendingAnalysisPage from './pages/SpendingAnalysisPage';
import SettingsPage from './pages/SettingsPage';
import AddTransactionPage from './pages/AddTransactionPage';
import TransactionsPage from './pages/TransactionsPage';
import AuthPage from './pages/AuthPage';
import Navbar from './components/Navbar';

// Auth protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAppContext();

  // While checking authentication status, show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" message="Checking authentication..." />
      </div>
    );
  }

  // If not authenticated, redirect to auth page
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Otherwise, render the children
  return children;
};

// Root route that redirects to dashboard if authenticated, otherwise to auth
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAppContext();
  const [showLoading, setShowLoading] = useState(true);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    // If still loading after 5 seconds, redirect to auth anyway
    const timer = setTimeout(() => {
      if (loading) {
        console.log("Authentication check timed out, redirecting to auth page");
        setShowLoading(false);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [loading]);

  // When loading is complete or times out
  useEffect(() => {
    if (!loading) {
      setShowLoading(false);
    }
  }, [loading]);

  // While checking authentication status and within timeout, show loading state
  if (loading && showLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" message="Checking authentication..." />
      </div>
    );
  }

  // Redirect based on authentication status (or if timed out)
  return <Navigate to={isAuthenticated ? "/dashboard" : "/auth"} replace />;
};

function App() {
  return (
    <AppProvider>
      <Router>
        <Routes>
          {/* Root path that redirects to appropriate location */}
          <Route path="/" element={<RootRedirect />} />

          {/* Authentication page - no navbar */}
          <Route path="/auth" element={<AuthPage />} />

          {/* Dashboard route with navbar */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="pt-16">
                  <Dashboard />
                </div>
              </>
            </ProtectedRoute>
          } />

          {/* Other protected routes with navbar */}
          <Route path="/fun-money" element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="pt-16">
                  <FunMoneyPage />
                </div>
              </>
            </ProtectedRoute>
          } />

          <Route path="/spending-analysis" element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="pt-16">
                  <SpendingAnalysisPage />
                </div>
              </>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="pt-16">
                  <SettingsPage />
                </div>
              </>
            </ProtectedRoute>
          } />

          <Route path="/add-transaction" element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="pt-16">
                  <AddTransactionPage />
                </div>
              </>
            </ProtectedRoute>
          } />

          <Route path="/transactions" element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="pt-16">
                  <TransactionsPage />
                </div>
              </>
            </ProtectedRoute>
          } />

          {/* Redirect any unknown routes to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AppProvider>
  );
}

export default App;